import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Lock, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail, inr } from "@/lib/api";

const Ctx = createContext({ lock: null, reload: () => {} });

export function FinanceLockProvider({ children }) {
  const [lock, setLock] = useState(null);
  const reload = useCallback(() => api.get("/finance-lock").then((r) => setLock(r.data)).catch(() => setLock(null)), []);
  useEffect(() => { reload(); }, [reload]);
  return <Ctx.Provider value={{ lock, reload }}>{children}</Ctx.Provider>;
}

export const useFinanceLock = () => useContext(Ctx);

export function useMoney() {
  const { lock } = useFinanceLock();
  const hide = !!(lock?.required && lock?.hide_elsewhere);
  return { hide, money: (v) => (hide ? "₹ •••••" : inr(v)) };
}

function PinSetup({ onDone }) {
  const [lp, setLp] = useState("");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/finance-lock/pin", { login_password: lp, pin }); onDone(); }
    catch (x) { setErr(formatApiErrorDetail(x.response?.data?.detail)); }
  };
  return (
    <form onSubmit={save} className="space-y-3" data-testid="finance-pin-setup">
      <p className="text-sm text-zinc-500">Create your personal finance PIN (4–8 digits). Confirm with your login password.</p>
      <input data-testid="finance-pin-login-password" type="password" value={lp} onChange={(e) => setLp(e.target.value)} placeholder="Your login password" className="h-11 w-full rounded-lg border border-zinc-200 px-3 text-sm outline-none focus:border-[#A9792C]" />
      <input data-testid="finance-pin-new" inputMode="numeric" type="password" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="New PIN" className="h-11 w-full rounded-lg border border-zinc-200 px-3 text-sm tracking-[0.4em] outline-none focus:border-[#A9792C]" />
      {err && <div data-testid="finance-pin-error" className="text-xs text-rose-600">{err}</div>}
      <Button data-testid="finance-pin-save" type="submit" className="w-full" disabled={!lp || pin.length < 4}>Save PIN</Button>
    </form>
  );
}

function LockScreen({ lock, title, onUnlock, reload, inline }) {
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const label = lock.mode === "shared" ? "Finance password" : lock.mode === "pin" ? "Your finance PIN" : "Your login password";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await api.post("/finance-lock/unlock", { password: pw }); onUnlock(); }
    catch (x) { setErr(formatApiErrorDetail(x.response?.data?.detail)); setPw(""); }
    setBusy(false);
  };
  return (
    <div data-testid={inline ? "finance-inline-lock" : "finance-lock-screen"} className={inline ? "rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-4" : "flex min-h-[70vh] items-center justify-center"}>
      <div className={inline ? "" : "w-full max-w-sm rounded-2xl border border-zinc-200 bg-white p-7 shadow-sm"}>
        <div className="mb-4 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-zinc-900 text-[#E8C877]"><Lock className="h-5 w-5" /></span>
          <div><div className="font-display text-lg font-bold text-zinc-900">{title}</div><div className="text-xs text-zinc-500">Protected finance area</div></div>
        </div>
        {lock.mode === "pin" && !lock.my_pin_set ? <PinSetup onDone={reload} /> : (
          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <input autoFocus={!inline} data-testid="finance-unlock-input" type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} placeholder={label}
                className="h-11 w-full rounded-lg border border-zinc-200 px-3 pr-10 text-sm outline-none focus:border-[#A9792C]" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-3 text-zinc-400" data-testid="finance-unlock-toggle">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
            </div>
            {err && <div data-testid="finance-unlock-error" className="text-xs text-rose-600">{err}</div>}
            <Button data-testid="finance-unlock-submit" type="submit" className="w-full" disabled={!pw || busy}><ShieldCheck className="mr-1 h-4 w-4" />{busy ? "Checking…" : "Unlock"}</Button>
            {lock.mode === "shared" && <p className="text-[11px] text-zinc-400">Ask the admin for the finance password.</p>}
          </form>
        )}
      </div>
    </div>
  );
}

export function FinanceGate({ title, children, inline = false }) {
  const { lock, reload } = useFinanceLock();
  const [open, setOpen] = useState(false);
  if (!lock) return inline ? null : <div className="p-8 text-sm text-zinc-400">Loading…</div>;
  if (inline && !lock.hide_elsewhere) return children;
  if (!lock.required || open) return children;
  return <LockScreen lock={lock} title={title} inline={inline} onUnlock={() => setOpen(true)} reload={reload} />;
}
